import './donation.css';

import { PillBar } from '../../../../shared/PillBar/pill-bar';
import { IPillBarData, IPillBarDataButton } from '../../../../shared/PillBar/pill-bar.interface';
import { Data } from '../../../../../models/data';
import { Order } from '../../../../../models/order';
import { Util } from '../../../../../utils/util';
import { IDataDonation } from '../../../../../models/data.interface';
import { Context } from '../../../../../models/context';
import { LocalizationService } from '../../../../../services/localization-service';

/**
 * Handles the rendering and functionality of the donation section during checkout.
 */
export class Donation {
    private readonly _containerElement: HTMLElement;
    private readonly _data: Data;
    private readonly _context: Context;
    private readonly _localizationService: LocalizationService;
    private readonly _order: Order;

    /**
     * Creates an instance of the Donation class.
     * @param {Data} data - The data object containing donation-related information.
     * @param {Order} order - The order object used to apply donations.
     */
    constructor(data: Data, context: Context, localizationService: LocalizationService, order: Order) {
        this._data = data;
        this._context = context;
        this._localizationService = localizationService;
        this._order = order;
        this._containerElement = this._createHtmlElement();
        this._containerElement.addEventListener("click", this._onLearnMoreLinkClick);
    }

    /**
     * Returns the root container element for the donation section.
     * @returns {HTMLElement} - The container element for the donation section.
     */
    public get element(): HTMLElement {
        return this._containerElement;
    }

    /**
     * Creates and returns the HTML structure for the donation section.
     * @returns {HTMLElement} - The generated HTML element for the donation section.
     */
    private _createHtmlElement(): HTMLElement {
        const html = `
            <div class="checkout-container">
                <div class="checkout-header">
                    <h2 ltag="Donation">${this._localizationService.names("Donation")}</h2>
                </div>
                <div id="div_checkout_donation_items" class="donations-section"></div>
            </div>`;

        return Util.createHtmlElementFromTemplate(html);
    }

    /**
     * Initializes the donation section by rendering available donation options.
     * If no donations are available, the section is hidden.
     */
    public init(): void {
        const parentDiv = this._containerElement.querySelector("#div_checkout_donation_items") as HTMLElement;
        parentDiv.innerHTML = "";

        const donations = this._data.getProperty("Donations");
        for (const donation of donations) {
            const div = this._createDonationCard(donation);
            parentDiv.append(div);
        }
    }

    /**
     * Creates a donation card element with relevant details and options.
     * @param {IDataDonation} donation - The donation data used to create the card.
     * @returns {HTMLElement} - The generated donation card element.
     */
    private _createDonationCard(donation: IDataDonation): HTMLElement {
        const name = this._localizationService.GetNewName(donation.Names);
        const description = this._localizationService.GetNewName(donation.Descriptions);

        const hasImage = (donation.ImageUrl && donation.ImageUrl !== "");
        const imageUrl = hasImage ? this._createDonationImageSection(donation, name) : "";
        const learnMoreLink = (!hasImage && donation.RedirectURL) ? this._createDonationLearnMoreLink(donation.RedirectURL, name) : "";
        const donationOptions = this._createDonationOptions(donation);

        const html = `
            <div id="div_checkout_donation_${donation.DonationId}" ${hasImage ? `class="donation-item"` : ""}>
                <div class="donation-item-info">
                    ${imageUrl}
                    <div class="donation-item-text">
                        <span id="span_checkout_donation_name_${donation.DonationId}" ltagj="${Util.toLtagj(donation.Names)}">${name}</span>
                        ${description ? `<span ltagj="${Util.toLtagj(donation.Descriptions)}">${description}</span>` : ""}
                        ${learnMoreLink}
                    </div>
                </div>
                <div id="div_checkout_donation_options_${donation.DonationId}" class="donation-options"></div>
            </div>`;

        const div = Util.createHtmlElementFromTemplate(html);
        const options = div.querySelector(`#div_checkout_donation_options_${donation.DonationId}`);
        if (options)
            options.appendChild(donationOptions);

        return div;
    }

    /**
     * Creates an image section for the donation UI element.
     * @param {IDataDonation} donation - The donation data object.
     * @param {string} name - The name of the donation.
     * @returns {HTMLElement} - The image section element.
     */
    private _createDonationImageSection(donation: IDataDonation, name: string): string {
        const redirectUrlHtml = this._createDonationLearnMoreLink(donation.RedirectURL, name);

        const html = `
            <div class="donation-item-image">
                <img src="${donation.ImageUrl || ""}" alt="${name} donation logo" role="presentation" />
                ${redirectUrlHtml}
            </div>`;

        return html;
    }

    private _createDonationLearnMoreLink(redirectUrl: string | null, name: string): string {
        if (!redirectUrl)
            return "";

        return `<a href="${redirectUrl}" target="_blank" rel="noopener noreferrer" aria-label="Learn more about the ${name} donation" ltag="LearnMore">${this._localizationService.names("LearnMore")}</a>`;
    }

    /**
     * The "Learn more" link above is a plain target="_blank" anchor to an arbitrary third-party
     * (donation partner) URL -- inside the wrapped app that would hand the native jsBridge-attached
     * WebView to that external host, so in-app clicks are routed through the native
     * openExternalLink bridge instead. Delegated on the root container since donation cards are
     * re-created on every init().
     */
    private readonly _onLearnMoreLinkClick = (event: MouseEvent): void => {
        if (!Util.isAppView() || typeof openExternalLink !== "function")
            return;

        const anchor = (event.target as HTMLElement)?.closest('a[ltag="LearnMore"]') as HTMLAnchorElement | null;
        if (!anchor)
            return;

        event.preventDefault();
        openExternalLink(anchor.href);
    }

    /**
     * Creates the donation options (radio buttons) for the donation UI element.
     * @param {IDataDonation} donation - The donation data object.
     * @returns {HTMLElement} - The donation options element.
     */
    private _createDonationOptions(donation: IDataDonation): HTMLElement {
        this._order.createDonation(donation.DonationId);

        const data: IPillBarData = {
            buttonId: `checkout_donations_${donation.DonationId}`,
            ariaLabelledBy: `span_checkout_donation_name_${donation.DonationId}`,
            buttons: []
        };

        // Rounded Up Option
        if (donation.IsRounded) {
            const roundedButton: IPillBarDataButton = {
                id: "round_up",
                text: this._localizationService.names("RoundUp"),
                selected: false,
                ltag: "RoundUp",
                function: () => this._calculateDonation(donation.DonationId)
            };
            data.buttons.push(roundedButton);
        }

        const country = this._data.getProperty("StoreInfo").country;
        // Amount 1 Option
        if (donation.Amount1) {
            const amount1Button: IPillBarDataButton = {
                id: "amount_1",
                text: Util.formatMoney(donation.Amount1, country),
                selected: false,
                function: () => this._calculateDonation(donation.DonationId, donation.Amount1)
            };
            data.buttons.push(amount1Button);
        }

        // Amount 2 Option
        if (donation.Amount2) {
            const amount2Button: IPillBarDataButton = {
                id: "amount_2",
                text: Util.formatMoney(donation.Amount2, country),
                selected: false,
                function: () => this._calculateDonation(donation.DonationId, donation.Amount2)
            };
            data.buttons.push(amount2Button);
        }

        // Amount 3 Option
        if (donation.Amount3) {
            const amount3Button: IPillBarDataButton = {
                id: "amount_3",
                text: Util.formatMoney(donation.Amount3, country),
                selected: false,
                function: () => this._calculateDonation(donation.DonationId, donation.Amount3)
            };
            data.buttons.push(amount3Button);
        }

        // No Thanks Option
        const noThanksButton: IPillBarDataButton = {
            id: "no_thanks",
            text: this._localizationService.names("NoThanks"),
            selected: true,
            ltag: "NoThanks",
            function: () => this._calculateDonation(donation.DonationId, 0)
        };
        data.buttons.push(noThanksButton);

        const pillBar = new PillBar(data, this._localizationService);
        return pillBar.element;
    }

    /**
     * Calculates and updates the donation amount based on the selected option.
     * If no amount is provided, it rounds up the total order amount.
     * @param {number} id - The donation ID.
     * @param {number} [amount] - The amount to donate (optional).
     */
    private _calculateDonation = (id: number, amount?: number): void => {
        if (amount === undefined) {
            const noDonationOrder = this._order.getProperty("Total");
            amount = Number(Util.formatNumber(Math.ceil(noDonationOrder) - noDonationOrder));
        }

        this._order.updateDonationAmount(id, amount);
    }
}